import { useEffect, useState, useCallback } from 'react';
import api from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from 'sonner';

const STATUS_COLORS = {
  pending: 'bg-blue-500/20 text-blue-300',
  running: 'bg-yellow-500/20 text-yellow-300',
  done: 'bg-green-500/20 text-green-300',
  failed: 'bg-red-500/20 text-red-300',
  dead: 'bg-red-900/40 text-red-400',
};

export default function JobsPage() {
  const [jobs, setJobs] = useState([]);
  const [stats, setStats] = useState(null);
  const [filterStatus, setFilterStatus] = useState('');
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(new Set());

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [j, s] = await Promise.all([
        api.get('/jobs', { params: filterStatus ? { status: filterStatus, limit: 200 } : { limit: 200 } }),
        api.get('/jobs/_stats/summary'),
      ]);
      setJobs(j.data.items || []);
      setStats(s.data);
    } catch (e) { toast.error(e.response?.data?.detail || 'Błąd'); }
    finally { setLoading(false); }
  }, [filterStatus]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { const t = setInterval(load, 15000); return () => clearInterval(t); }, [load]);

  const retry = async (id) => {
    try {
      await api.post(`/jobs/${id}/retry`);
      toast.success('Zretryowane');
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Błąd'); }
  };

  const toggle = (id) => setExpanded(p => { const n = new Set(p); n.has(id) ? n.delete(id) : n.add(id); return n; });

  return (
    <div className="p-6 space-y-4">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-heading font-semibold">Task Queue</h1>
          <p className="text-sm text-muted-foreground">Background jobs — kampanie, agent, retry itd. (auto-refresh co 15s)</p>
        </div>
        <Button variant="outline" onClick={load}>Odśwież</Button>
      </div>

      {/* STATS */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
          {Object.entries(stats.status_counts || {}).map(([s, n]) => (
            <div key={s} className={`p-3 rounded border ${STATUS_COLORS[s] || 'border-border/40'} cursor-pointer`} onClick={() => setFilterStatus(s === filterStatus ? '' : s)}>
              <div className="text-2xl font-mono font-semibold">{n}</div>
              <div className="text-xs opacity-80">{s}</div>
            </div>
          ))}
        </div>
      )}

      {stats?.registered_handlers?.length > 0 && (
        <div className="text-xs opacity-60">
          Zarejestrowane task typy: {stats.registered_handlers.map(h => <Badge key={h} variant="outline" className="text-[10px] mx-0.5">{h}</Badge>)}
        </div>
      )}

      <div className="flex gap-2 items-center">
        <span className="text-xs opacity-60">filtr:</span>
        {['', 'pending', 'running', 'done', 'failed', 'dead'].map(s => (
          <Badge key={s || 'all'} variant={filterStatus === s ? 'default' : 'outline'} className="cursor-pointer" onClick={() => setFilterStatus(s)}>
            {s || 'wszystkie'}
          </Badge>
        ))}
      </div>

      {loading ? <Skeleton className="h-40" /> : jobs.length === 0 ? (
        <Card><CardContent className="p-6 text-center text-muted-foreground">Brak jobs dla wybranego filtra.</CardContent></Card>
      ) : (
        <div className="space-y-1">
          {jobs.map(j => (
            <Card key={j.id} className="cursor-pointer" onClick={() => toggle(j.id)}>
              <CardContent className="p-3">
                <div className="flex items-center gap-2 text-sm">
                  <span className={`text-[10px] px-2 py-0.5 rounded font-mono ${STATUS_COLORS[j.status] || ''}`}>{j.status}</span>
                  <span className="font-mono text-xs opacity-70">{j.id.slice(0, 8)}</span>
                  <span className="font-medium">{j.task_name}</span>
                  <Badge variant="outline" className="text-[10px]">próba {j.attempt}/{j.max_attempts}</Badge>
                  <span className="text-xs opacity-60 ml-auto">{(j.created_at || '').slice(0, 19).replace('T', ' ')}</span>
                  {(j.status === 'failed' || j.status === 'dead') && (
                    <Button variant="outline" size="sm" onClick={(e) => { e.stopPropagation(); retry(j.id); }}>Retry</Button>
                  )}
                </div>
                {expanded.has(j.id) && (
                  <div className="mt-2 space-y-1 text-xs">
                    {j.next_run_at && <div><b>next_run_at:</b> {j.next_run_at.slice(0, 19).replace('T', ' ')}</div>}
                    {j.started_at && <div><b>started:</b> {j.started_at.slice(0, 19).replace('T', ' ')}</div>}
                    {j.completed_at && <div><b>completed:</b> {j.completed_at.slice(0, 19).replace('T', ' ')}</div>}
                    {j.last_error && <pre className="text-[10px] bg-destructive/10 p-2 rounded overflow-auto max-h-32 whitespace-pre-wrap">{j.last_error}</pre>}
                    {j.result && <div><b>result:</b> <pre className="text-[10px] bg-muted/30 p-1 rounded inline-block">{JSON.stringify(j.result).slice(0, 500)}</pre></div>}
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
